import React, { useState } from 'react';
import { api } from '../api';

const TransactionImportExport = ({ user, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('append');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('export');

  const isBoard = user?.role && ['president', 'vice_president', 'treasurer', 'secretary', 'board_member', 'platform_admin'].includes(user.role);

  const downloadTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/budget/transactions/template', {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transaction_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      alert('Failed to download template: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const exportTransactions = async () => {
    if (!startDate || !endDate) {
      alert('Please select start and end dates');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get('/api/budget/transactions/export', {
        params: { start_date: startDate, end_date: endDate },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export transactions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const previewImport = async () => {
    if (!file || !startDate || !endDate) {
      alert('Please select a file and date range');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('mode', mode);
      formData.append('file', file);

      const response = await api.post('/api/budget/transactions/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreview(response.data);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to preview import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const commitImport = async () => {
    if (!file || !startDate || !endDate) {
      alert('Please select a file and date range');
      return;
    }

    if (mode === 'replace' && !window.confirm('This will replace ALL transactions in the selected period. Are you sure?')) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('start_date', startDate);
      formData.append('end_date', endDate);
      formData.append('mode', mode);
      formData.append('file', file);

      const response = await api.post('/api/budget/transactions/import/commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(response.data.message);
      setPreview(null);
      setFile(null);
      setIsOpen(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import transactions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        📥 Import/Export
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Transaction Import/Export</h2>
            <button
              onClick={() => {
                setIsOpen(false);
                setPreview(null);
                setFile(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('export')}
                className={`py-2 px-1 border-b-2 text-sm font-medium ${
                  activeTab === 'export'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Export Transactions
              </button>
              {isBoard && (
                <button
                  onClick={() => setActiveTab('import')}
                  className={`py-2 px-1 border-b-2 text-sm font-medium ${
                    activeTab === 'import'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Import Transactions
                </button>
              )}
            </nav>
          </div>

          {/* Date Range Selector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-800 mb-2">📊 Export Transactions</h3>
                <p className="text-xs text-blue-600 mb-4">
                  Export your financial transactions to CSV format for external analysis or backup.
                  The exported file will include all transaction fields with budget item names resolved.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={exportTransactions}
                    disabled={loading || !startDate || !endDate}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
                  >
                    {loading ? 'Exporting...' : '📤 Export CSV'}
                  </button>
                  <button
                    onClick={downloadTemplate}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium w-full sm:w-auto"
                  >
                    📋 Download Template
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Import Tab - Board Members Only */}
          {activeTab === 'import' && isBoard && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">⚠️ Import Instructions</h3>
                <div className="text-xs text-yellow-700 space-y-1">
                  <p><strong>Before importing:</strong></p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Download the template CSV file - it includes all your budget items!</li>
                    <li>Dates can be simple like <strong>12/1/2025</strong> or <strong>2025-12-01</strong></li>
                    <li>Use <strong>positive amounts</strong> for both income and expenses</li>
                    <li>Set the <strong>category</strong> field to "income" or "expense" to classify transactions</li>
                    <li>Reference numbers help prevent duplicates</li>
                    <li>Budget item names must match exactly (use template values)</li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Import Mode</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="append">Append (safe - skip duplicates)</option>
                    <option value="merge">Merge (update matching references)</option>
                    <option value="replace">Replace (⚠️ delete all in period)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {mode === 'append' && 'Add new transactions, skip duplicates'}
                    {mode === 'merge' && 'Add new + update existing with same reference'}
                    {mode === 'replace' && 'Delete all transactions in period, then add from CSV'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={previewImport}
                  disabled={loading || !file || !startDate || !endDate}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
                >
                  {loading ? 'Previewing...' : '👁️ Preview Import'}
                </button>
                {preview && preview.counts.errors === 0 && (
                  <button
                    onClick={commitImport}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 w-full sm:w-auto"
                  >
                    {loading ? 'Importing...' : '✅ Commit Import'}
                  </button>
                )}
              </div>

              {/* Preview Results */}
              {preview && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Import Preview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{preview.counts.new}</div>
                      <div className="text-xs text-gray-600">New</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-yellow-600">{preview.counts.duplicates}</div>
                      <div className="text-xs text-gray-600">Duplicates</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{preview.counts.conflicts}</div>
                      <div className="text-xs text-gray-600">Conflicts</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{preview.counts.errors}</div>
                      <div className="text-xs text-gray-600">Errors</div>
                    </div>
                  </div>

                  {/* Error Details Table */}
                  {preview.counts.errors > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                      <h4 className="text-sm font-medium text-red-800 mb-2">❌ Errors Found:</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-red-300">
                              <th className="text-left py-1 px-2 text-red-700">Row</th>
                              <th className="text-left py-1 px-2 text-red-700">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview.errors.map((error, idx) => (
                              <tr key={idx} className="border-b border-red-200">
                                <td className="py-1 px-2 text-red-600 font-mono">{error.split(':')[0]}</td>
                                <td className="py-1 px-2 text-red-700">{error.split(':').slice(1).join(':').trim()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Conflicts Details Table */}
                  {preview.counts.conflicts > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">⚠️ Reference Conflicts (will update existing):</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-blue-300">
                              <th className="text-left py-1 px-2 text-blue-700">Reference</th>
                              <th className="text-left py-1 px-2 text-blue-700">Incoming</th>
                              <th className="text-left py-1 px-2 text-blue-700">Existing</th>
                              <th className="text-left py-1 px-2 text-blue-700">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview.conflicts.map((conflict, idx) => (
                              <tr key={idx} className="border-b border-blue-200">
                                <td className="py-1 px-2 text-blue-600 font-mono">{conflict.incoming.reference}</td>
                                <td className="py-1 px-2 text-blue-700">
                                  {conflict.incoming.description} (${conflict.incoming.amount})
                                </td>
                                <td className="py-1 px-2 text-blue-700">
                                  {conflict.existing.description} (${conflict.existing.amount})
                                </td>
                                <td className="py-1 px-2 text-blue-600">
                                  {mode === 'merge' ? 'Update categories/notes' : 'Skip'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Duplicates Details Table */}
                  {preview.counts.duplicates > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                      <h4 className="text-sm font-medium text-yellow-800 mb-2">🔄 Duplicates (will be skipped):</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-yellow-300">
                              <th className="text-left py-1 px-2 text-yellow-700">Date</th>
                              <th className="text-left py-1 px-2 text-yellow-700">Description</th>
                              <th className="text-left py-1 px-2 text-yellow-700">Amount</th>
                              <th className="text-left py-1 px-2 text-yellow-700">Vendor</th>
                              <th className="text-left py-1 px-2 text-yellow-700">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview.duplicates.map((duplicate, idx) => (
                              <tr key={idx} className="border-b border-yellow-200">
                                <td className="py-1 px-2 text-yellow-700 font-mono">{duplicate.transaction_date}</td>
                                <td className="py-1 px-2 text-yellow-700">{duplicate.description}</td>
                                <td className="py-1 px-2 text-yellow-700">${duplicate.amount}</td>
                                <td className="py-1 px-2 text-yellow-700">{duplicate.vendor}</td>
                                <td className="py-1 px-2 text-yellow-600">Skip (duplicate)</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* New Transactions Preview */}
                  {preview.counts.new > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
                      <h4 className="text-sm font-medium text-green-800 mb-2">✅ New Transactions (sample - first 5):</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-green-300">
                              <th className="text-left py-1 px-2 text-green-700">Date</th>
                              <th className="text-left py-1 px-2 text-green-700">Category</th>
                              <th className="text-left py-1 px-2 text-green-700">Description</th>
                              <th className="text-left py-1 px-2 text-green-700">Amount</th>
                              <th className="text-left py-1 px-2 text-green-700">Reference</th>
                            </tr>
                          </thead>
                          <tbody>
                            {preview.preview.new.slice(0, 5).map((transaction, idx) => (
                              <tr key={idx} className="border-b border-green-200">
                                <td className="py-1 px-2 text-green-700 font-mono">{transaction.transaction_date}</td>
                                <td className="py-1 px-2 text-green-700">
                                  <span className={`px-1 py-0.5 rounded text-xs ${
                                    transaction.category === 'income' 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {transaction.category}
                                  </span>
                                </td>
                                <td className="py-1 px-2 text-green-700">{transaction.description}</td>
                                <td className="py-1 px-2 text-green-700">${transaction.amount}</td>
                                <td className="py-1 px-2 text-green-700 font-mono">{transaction.reference || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {preview.counts.new > 5 && (
                          <p className="text-xs text-green-600 mt-2">...and {preview.counts.new - 5} more transactions</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-600">
                    <p><strong>Summary:</strong> {preview.counts.total_processed} transactions processed</p>
                    <p>Mode: <strong>{preview.mode}</strong> | Period: <strong>{preview.period.start} to {preview.period.end}</strong></p>
                    {preview.counts.errors === 0 ? (
                      <p className="text-green-600 font-medium mt-2">✅ Ready to import - no errors found!</p>
                    ) : (
                      <p className="text-red-600 font-medium mt-2">❌ Please fix errors before importing</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionImportExport;